// Wire types for the calibration case submit + poll (Story 8.10, FR41).
// Mirrors the calibration backend schemas:
//   src/application/calibration/submit_calibration_case/schemas.py
//   src/application/calibration/get_case_submission_status/schemas.py
// snake_case on the wire (no FE rename); values arrive envelope-unwrapped by apiFetch.

/**
 * Body of POST /calibration/cases/{case_id}/submit.
 *
 * `task_code` is SINGULAR — one task per case (Story 4.6 Decision #3) — and must
 * be the case's OWN task or the server refuses with CAL-422-TASK. It comes from
 * the board wire's `cases[].task_code` (Story 8.10 AC1) and from nowhere else;
 * in particular NEVER from the gold-standard endpoint, which is the answer key.
 *
 * `scores` is POSITIONAL over the clinician 35-item ADL order (`clinicianAdlItems`)
 * — the same order the score grid lays out, so no re-mapping. `null` marks an
 * unscored item; at most one may be null (the 34/35 minimum).
 */
export interface SubmitCalibrationCaseRequest {
  task_code: string;
  scores: (number | null)[];
}

/** 202 body — the submission is accepted and queued for the scoring worker. */
export interface SubmitCalibrationCaseData {
  submission_id: string;
  case_id: string;
  status: "queued";
}

/**
 * Poll status for one submitted case.
 *
 * TRAP: the submit 202 reports `queued` but this wire never does — the same state
 * is called `in_progress` here. Branch on THESE literals only.
 *
 * Second trap: the BOARD wire (`GET /calibration/status`) maps a `failed` attempt
 * back to `pending` (re-submittable, Story 8.3 D7) while this wire surfaces
 * `failed` verbatim. That asymmetry is what lets the scoring view re-enable submit
 * after a failed scoring run.
 */
export type CaseSubmissionStatus = "pending" | "in_progress" | "scored" | "failed";

export interface CaseSubmissionStatusData {
  case_id: string;
  status: CaseSubmissionStatus;
}

/**
 * Terminal poll states — stop polling.
 *
 * Deliberately NOT `isTerminalStatus` from `scoring-types.ts`: that predicate is
 * typed to `ScoringJobStatusValue` (`queued|processing|completed|failed|blocked`),
 * so `"scored"` is neither assignable to it nor in its terminal set. Widening the
 * scoring type to fit calibration would couple two independent wires.
 */
export function isTerminalCaseStatus(
  status: CaseSubmissionStatus | undefined,
): boolean {
  return status === "scored" || status === "failed";
}
